import { ReactNode } from "react";
import { AlertCircle, Inbox, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

type StateType = "loading" | "empty" | "error";

interface StateViewProps {
  state: StateType;
  title?: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}

const defaultLabels: Record<StateType, { title: string; description: string }> = {
  loading: {
    title: "Chargement",
    description: "Récupération des données en cours.",
  },
  empty: {
    title: "Aucune donnée disponible",
    description: "Ajoutez un premier élément pour démarrer.",
  },
  error: {
    title: "Une erreur est survenue",
    description: "Veuillez réessayer dans quelques instants.",
  },
};

export default function StateView({
  state,
  title,
  description,
  action,
  className,
}: StateViewProps) {
  const labels = defaultLabels[state];

  return (
    <div
      className={cn(
        "flex min-h-[260px] w-full items-center justify-center rounded-lg border border-border/70 bg-card",
        className
      )}
      role={state === "error" ? "alert" : "status"}
      aria-live={state === "error" ? "assertive" : "polite"}
    >
      <div className="mx-auto max-w-md space-y-3 px-6 py-8 text-center">
        <div className="flex justify-center text-muted-foreground">
          {state === "loading" && <Loader2 className="h-6 w-6 animate-spin" />}
          {state === "empty" && <Inbox className="h-6 w-6" />}
          {state === "error" && <AlertCircle className="h-6 w-6" />}
        </div>
        <h2 className="text-base font-semibold text-foreground">{title ?? labels.title}</h2>
        <p className="text-sm text-muted-foreground">{description ?? labels.description}</p>
        {action && <div className="flex justify-center pt-1">{action}</div>}
      </div>
    </div>
  );
}
